import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mailbox, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Account created");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-10 flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary text-white">
            <Mailbox className="h-5 w-5" />
          </div>
          <div>
            <p className="font-heading text-xl font-bold tracking-tighter leading-none">MailShift</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Migration Engine</p>
          </div>
        </div>

        <h1 className="font-heading text-4xl font-bold tracking-tighter">Create account</h1>
        <p className="mt-3 text-muted-foreground">Start migrating email in minutes.</p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-xs uppercase tracking-[0.15em]">Name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)}
                   required data-testid="register-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em]">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   required data-testid="register-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em]">Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                   required minLength={6} data-testid="register-password-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full gap-2" data-testid="register-submit-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            Create account
          </Button>
        </form>

        <p className="mt-6 text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-primary hover:underline" data-testid="go-login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
